import { Container, createTheme, Modal, rem, type MantineColorsTuple } from '@mantine/core';

/**
 * Mantine theme for Baluarte CRM — applies the official Baluarte brand palette.
 *
 * Brand colours (from the firm's reference palette slide):
 *   - Azul Baluarte (primary)   100% = #006595  RGB(0,101,149)
 *   - Cinzento (neutral grey)
 *   - Verde       (semantic: success / RTO ok)
 *   - Amarelo     (semantic: warning)
 *   - Laranja     (semantic: attention)
 *   - Bordeaux    (semantic: high-risk / destructive accent)
 *   - Azul Petroleo (secondary / informational accent)
 *
 * Mantine palettes are 10-stop tuples (0=lightest tint - 9=darkest shade).
 * Stops were derived by mixing the 100% colour with white (lighter stops)
 * and with black (darker stops) so the firm's published 100/80/60/40/20%
 * shades land on stops 9, 7, 5, 3 and 1 respectively.
 */
const azulBaluarte: MantineColorsTuple = [
  '#e6f0f4', '#cce2ea', '#b2d2e0', '#99c3d5', '#80b2ca',
  '#66a3c0', '#3384aa', '#006595', '#00557d', '#004566',
];

const cinzento: MantineColorsTuple = [
  '#f7f7f8', '#eef0f2', '#dde2e6', '#c4ccd3', '#9aa6af',
  '#6f7d88', '#4d5a64', '#3a464f', '#2a343b', '#1c252b',
];

const verde: MantineColorsTuple = [
  '#ecf6ee', '#d6ecda', '#aedab6', '#84c790', '#62b870',
  '#48ad59', '#3a9c4b', '#2c823b', '#246a30', '#1d5527',
];

const amarelo: MantineColorsTuple = [
  '#fffaeb', '#fff3cc', '#ffe699', '#ffd866', '#ffc933',
  '#ffba00', '#cc9500', '#996f00', '#664a00', '#332500',
];

const laranja: MantineColorsTuple = [
  '#fff1e6', '#ffd9bf', '#ffbf94', '#ffa468', '#ff8a3d',
  '#ff7011', '#cc5a0e', '#99430a', '#662d07', '#331603',
];

const bordeaux: MantineColorsTuple = [
  '#fbeaed', '#f3c7ce', '#e89aa5', '#dc6c7b', '#cf3f54',
  '#b9293f', '#962033', '#741927', '#52121b', '#330a11',
];

const azulPetroleo: MantineColorsTuple = [
  '#e9f1f3', '#c8dde2', '#9ec4cd', '#74acb8', '#4d94a4',
  '#287d8f', '#206474', '#194d59', '#11353e', '#091d23',
];

export const theme = createTheme({
  primaryColor: 'azul',
  primaryShade: 7,
  defaultRadius: 'sm',
  // IBM Plex Sans is the primary face, matching the firm's reference
  // dashboard. Self-hosted from src/fonts.css (no Google Fonts CDN).
  // Nunito Sans is kept as a fallback (the closest free near-match to
  // Avenir LT Pro). Drop a licensed Avenir woff2 into src/assets/fonts/
  // and reorder if the firm prefers Avenir later.
  fontFamily: '"IBM Plex Sans", "Nunito Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontFamilyMonospace: '"IBM Plex Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  headings: { fontWeight: '600' },
  colors: {
    azul:        azulBaluarte,
    cinzento,
    verde,
    amarelo,
    laranja,
    bordeaux,
    petroleo:    azulPetroleo,
    green: verde,
    red:   bordeaux,
    gray:  cinzento,
  },
  components: {
    // Task #260 (2026-06-08): app-wide modal dismiss policy.
    // Pedro 2026-06-04 design call: "only modal buttons close, no
    // click-outside-to-dismiss." Default closeOnClickOutside to false
    // app-wide so stray clicks cannot dismiss destructive-confirm or
    // form-edit modals where the loss would be material. closeOnEscape
    // is left at the Mantine default (true) per WCAG ARIA dialog
    // convention — keyboard users rely on Escape, and it requires
    // intentional action. Per-modal opt-in to closeOnClickOutside=true
    // remains available for genuinely-dismissible info modals
    // (see CascadeDeleteModal.tsx for an example of explicit override).
    Modal: Modal.extend({
      defaultProps: {
        closeOnClickOutside: false,
      },
    }),
    // The app's page width (W1600, Pedro 2026-10-21: 2,130 px, "with the exceptions"): every
    // page is <Container size="xl">, a MAXIMUM — on screens narrower than ~2,400 px pages fill
    // the space. Exceptions keep their own width: Portfolios and Securities (fluid), Help (md),
    // Sign in. Undo = this one value; a single page opts out with `fluid`.
    Container: Container.extend({
      vars: (_theme, props) => ({
        root: { '--container-size': props.fluid ? '100%' : props.size === 'xl' ? rem(2130) : undefined },
      }),
    }),
  },
});

export const BRAND = {
  primary: '#006595',
  primaryDark: '#00557d',
  primaryLight: '#cce2ea',
  neutral: '#3a464f',
  neutralLight: '#dde2e6',
  surfaceTint: '#f7f9fb',
} as const;
